import React, { MouseEventHandler } from 'react';
import styled from 'styled-components';
import {
  IconPlaylist,
  IconAlbum,
  IconChevronDown,
  TypeList,
  LogoSpotify,
} from '@spotify-internal/encore-web';
import * as tokens from 'styles';
import { media } from 'styles/mixins';
import { getPlanName, NAME_LONG } from 'lib/i18n';
import { PrimaryButton } from 'components/Button/PrimaryButton';
import Ticket from 'components/Ticket';
import Type from 'components/Type';
import Benefit from 'components/SubLandingPage/Benefit';
import { PLAN_DUO } from 'lib/plans';
import { interpolate } from 'lib/interpolate';
import UnderlinedLink from 'components/Link/UnderlinedLink';
import { useTranslation } from 'data/translation/useTranslation';

const SectionInvitation = styled.section`
  position: relative;
  padding: 0 ${tokens.spacer8} ${tokens.spacer48};

  ${media.mobileMdUp} {
    padding-bottom: ${tokens.spacer64};
  }
`;

const ContentWrapper = styled.div`
  max-width: 422px;
  margin: 0 auto;

  > * + * {
    margin-top: ${tokens.spacer24};
  }
`;

const PlanWrapper = styled(Type.H1)`
  padding: 0 0 ${tokens.spacer4} 0;
  &:lang(ar) {
    padding-bottom: ${tokens.spacer8};
  }
`;

const ArrowDown = styled.div`
  width: 50px;
  height: 50px;
  background: ${tokens.white};
  color: ${tokens.royalBlue};
  box-shadow: 0 0 11px 0 rgba(0, 0, 0, 0.3);
  border-radius: 100%;
  padding: 14px ${tokens.spacer12};
  position: absolute;
  bottom: -${tokens.spacer24};
  left: calc(100vw - 25px - 50%);
  z-index: 2;
  ${media.mobileMdUp} {
    display: none;
  }
`;

const BenefitsList = styled(TypeList)`
  list-style: none;
  padding: 0 ${tokens.spacer32};

  > * {
    padding-bottom: 0;
  }

  > * + * {
    margin-top: 1em;
  }
`;

const TermsConditions = styled.div`
  margin-top: ${tokens.spacer12};
  ${media.mobileMdUp} {
    margin-top: ${tokens.spacer24};
  }
`;

type InvitationDisplayProps = {
  joinUrl: string;
  termsUrl: string;
  onJoinClick: MouseEventHandler;
  onTermsClick: MouseEventHandler;
};

const InvitationDisplay = ({
  joinUrl,
  termsUrl,
  onJoinClick,
  onTermsClick,
}: InvitationDisplayProps) => {
  const { t } = useTranslation();
  return (
    <SectionInvitation>
      <ContentWrapper>
        <Ticket>
          <LogoSpotify height={51} condensed />
          <PlanWrapper>{t('premium_duo')}</PlanWrapper>
          <Type.H2 variant="balladBold">
            {t('sub_landing_page.youre_invited')}
          </Type.H2>
        </Ticket>
        <BenefitsList>
          <Benefit
            IconComponent={IconPlaylist}
            text={t('sub_landing_page.list.listen_everywhere')}
          />
          <Benefit
            IconComponent={IconAlbum}
            text={t('sub_landing_page.list.keep_your_account_details')}
          />
        </BenefitsList>
        <PrimaryButton href={joinUrl} onClick={onJoinClick} $alternateDuo>
          {t('sub_landing_page.accept_invite')}
        </PrimaryButton>
        <TermsConditions>
          <Type.P variant="finale">
            {t('sub_landing_page.terms.no_refunds')}
          </Type.P>
          <Type.P variant="finale">
            {interpolate(t('sub_landing_page.terms.by_accepting_agree'), {
              plan_name_terms: (
                <UnderlinedLink href={termsUrl} onClick={onTermsClick}>
                  {t('sub_landing_page.plan_name_terms', {
                    plan_name: getPlanName(PLAN_DUO, NAME_LONG, t),
                  })}
                </UnderlinedLink>
              ),
            })}
          </Type.P>
        </TermsConditions>
      </ContentWrapper>
      <a
        href="#phoneDisplay"
        aria-label={t('sub_landing_page.read_more_about_premium_duo')}
      >
        <ArrowDown>
          <IconChevronDown iconSize={24} />
        </ArrowDown>
      </a>
    </SectionInvitation>
  );
};

export default InvitationDisplay;
